import {gql} from '@apollo/client';
import {getTestProps} from '@benchling/js-test-utils';
import {Button, createStyles, Group, Stack, Tabs, Text, Title} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import React from 'react';

import CanvasPreviewFrame from 'canvas-core/preview/CanvasPreviewFrame';
import useCanvasExpandedPreviewUrl from 'canvas-core/preview/useCanvasExpandedPreviewUrl';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import AgentFoundryAccessModal from 'llm/chat-library/foundries/AgentFoundryAccessModal';
import AgentFoundryAgents from 'llm/chat-library/foundries/AgentFoundryAgents';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {CenteredSpinner} from 'ui-library/Spinner';

import {
  AgentFoundryQuery as AgentFoundryQueryData,
  AgentFoundryQueryVariables,
} from './__generated/AgentFoundryQuery';

enum AgentFoundryTab {
  AGENTS = 'AGENTS',
  CANVAS = 'CANVAS',
}

const useStyles = createStyles((theme) => ({
  container: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing.md,
    height: '100%',
    minHeight: 0,
    padding: theme.spacing.xl,
  },
  tabsRoot: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
  },
  canvasPanel: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
    paddingTop: theme.spacing.md,
  },
  agentsPanel: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    paddingTop: theme.spacing.md,
  },
}));

// The routine fields the agents roster needs to render a row (name, schedule, status) are
// selected directly on `coordinatorAgent`/`agents.agent` here rather than via a separate
// routinesByIds lookup: both are already typed Routine references on AgentFoundry, so there is no
// separate id-resolution step to save a round trip for.
export const AGENT_FOUNDRY_QUERY = gql`
  query AgentFoundryQuery($id: StrictString!) @engComponent(name: LLM) @skipAudit {
    agentFoundry(id: $id) {
      __typename
      ... on AgentFoundryNode {
        id
        name
        description
        createdAt
        creatorName
        coordinatorAgent {
          id
          name
          triggerType
          triggerConfig
          status
        }
        agents {
          agent {
            id
            name
            triggerType
            triggerConfig
            status
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * The canvas backing an agent foundry, filling whatever height its container gives it. Reuses
 * useCanvasExpandedPreviewUrl and CanvasPreviewFrame -- the same signed-URL fetch and
 * iframe/sandbox setup chat-embedded canvases use (see ChatCanvasArtifactView) -- but renders only
 * the canvas contents, without any of that view's card chrome (icon tile, version badge, refresh
 * button), since this page already has its own header.
 *
 * Fetches by `agentFoundryId` so the render endpoint can authorize on the foundry itself, letting
 * collaborators without direct canvas-version access view it too. Any render failure (no canvas
 * configured, missing asset, etc.) surfaces as the same error banner below.
 */
function AgentFoundryCanvas({agentFoundryId}: {agentFoundryId: string}): JSX.Element {
  const {signedUrl, isLoading, errorMessage} = useCanvasExpandedPreviewUrl({agentFoundryId});

  if (isLoading) {
    return <CenteredSpinner />;
  }

  if (errorMessage != null) {
    return (
      <InlineBanner
        bannerType={InlineBannerType.ERROR}
        text={errorMessage}
        {...getTestProps({component: 'AgentFoundryPage', element: 'noCanvasBanner'})}
      />
    );
  }

  if (signedUrl == null) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to load the canvas." />;
  }

  return <CanvasPreviewFrame signedUrl={signedUrl} />;
}

/**
 * Detail view for a single Agent Foundry, rendered as its own workspace tab (see BufferUtil and
 * EditorRoutes.agentFoundryEditor) and addressable at its own URL (see AIRoutes): the foundry's name
 * and description, a "Manage access" button that opens AgentFoundryAccessModal, and tabs for the
 * canvas currently backing it and the agents provisioned for it. The Manage access button is shown
 * to any viewer who can load this page -- like RoutineAccessTab, the modal it opens renders
 * read-only for anyone without canManageCollaborators, rather than being hidden outright.
 */
export default function AgentFoundryPage({agentFoundryId}: {agentFoundryId: string}): JSX.Element {
  const {classes} = useStyles();
  const [isAccessModalOpen, {open: openAccessModal, close: closeAccessModal}] = useDisclosure(false);
  const queryResult = useBenchlingQuery<AgentFoundryQueryData, AgentFoundryQueryVariables>(
    AGENT_FOUNDRY_QUERY,
    {variables: {id: agentFoundryId}}
  );

  if (queryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }

  if (
    queryResult.type === QueryResultType.ERROR ||
    queryResult.data.agentFoundry == null ||
    isInaccessible(queryResult.data.agentFoundry)
  ) {
    return (
      <Stack spacing="md" className={classes.container}>
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text="This foundry could not be found, or you do not have access to it."
        />
      </Stack>
    );
  }

  const {agentFoundry} = queryResult.data;

  return (
    <div className={classes.container} {...getTestProps({component: 'AgentFoundryPage', element: 'root'})}>
      <Group position="apart" align="flex-start" noWrap={true}>
        <Stack spacing={4}>
          <Title order={3}>{agentFoundry.name}</Title>
          <Text c="dimmed">{agentFoundry.description || 'No description yet.'}</Text>
        </Stack>
        <Button
          variant="secondary"
          onClick={openAccessModal}
          {...getTestProps({component: 'AgentFoundryPage', element: 'manageAccessButton'})}
        >
          Manage access
        </Button>
      </Group>
      <Tabs defaultValue={AgentFoundryTab.CANVAS} keepMounted={false} className={classes.tabsRoot}>
        <Tabs.List>
          <Tabs.Tab
            value={AgentFoundryTab.CANVAS}
            {...getTestProps({component: 'AgentFoundryPage', element: 'tab', key: 'Overview'})}
            data-amp-safe={false}
          >
            Overview
          </Tabs.Tab>
          <Tabs.Tab
            value={AgentFoundryTab.AGENTS}
            {...getTestProps({component: 'AgentFoundryPage', element: 'tab', key: 'Agents'})}
            data-amp-safe={false}
          >
            Agents
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value={AgentFoundryTab.CANVAS} className={classes.canvasPanel}>
          <AgentFoundryCanvas agentFoundryId={agentFoundryId} />
        </Tabs.Panel>

        <Tabs.Panel value={AgentFoundryTab.AGENTS} className={classes.agentsPanel}>
          <AgentFoundryAgents coordinatorAgent={agentFoundry.coordinatorAgent} agents={agentFoundry.agents} />
        </Tabs.Panel>
      </Tabs>
      {isAccessModalOpen && (
        <AgentFoundryAccessModal agentFoundryId={agentFoundryId} onRequestClose={closeAccessModal} />
      )}
    </div>
  );
}
